var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { inject, provide, watch, shallowRef, computed, markRaw } from 'vue-demi';
import { isFn, isValid, lazyMerge } from '@formily/shared';
import { Schema } from '@formily/json-schema';
import { SchemaSymbol, SchemaOptionsSymbol, SchemaExpressionScopeSymbol, } from '../shared';
import { useField } from '../hooks';
import ObjectField from './ObjectField';
import ArrayField from './ArrayField';
import Field from './Field';
import VoidField from './VoidField';
import { h } from '../shared/h';
var resolveEmptySlot = function (slots) {
    return Object.keys(slots).length
        ? h('div', { style: 'display:contents;' }, slots)
        : undefined;
};
var RecursionField = {
    name: 'RecursionField',
    inheritAttrs: false,
    props: {
        schema: {
            required: true,
        },
        name: [String, Number],
        basePath: {},
        onlyRenderProperties: {
            type: Boolean,
            default: undefined,
        },
        onlyRenderSelf: {
            type: Boolean,
            default: undefined,
        },
        mapProperties: {},
        filterProperties: {},
    },
    setup: function (props) {
        var parentRef = useField();
        var optionsRef = inject(SchemaOptionsSymbol);
        var scopeRef = inject(SchemaExpressionScopeSymbol);
        var createSchema = function (schemaProp) {
            return markRaw(new Schema(schemaProp));
        };
        var fieldSchemaRef = computed(function () { return createSchema(props.schema); });
        var getPropsFromSchema = function (schema) {
            var _a;
            return (_a = schema === null || schema === void 0 ? void 0 : schema.toFieldProps) === null || _a === void 0 ? void 0 : _a.call(schema, __assign(__assign({}, optionsRef.value), { get scope() {
                    return lazyMerge(optionsRef.value.scope, scopeRef.value);
                } }));
        };
        var fieldPropsRef = shallowRef(getPropsFromSchema(fieldSchemaRef.value));
        watch([fieldSchemaRef, optionsRef], function () {
            fieldPropsRef.value = getPropsFromSchema(fieldSchemaRef.value);
        });
        var getBasePath = function () {
            var _a, _b, _c, _d;
            if (props.onlyRenderProperties) {
                return (_a = props.basePath) !== null && _a !== void 0 ? _a : (_b = parentRef === null || parentRef === void 0 ? void 0 : parentRef.value) === null || _b === void 0 ? void 0 : _b.address.concat(props.name);
            }
            return (_c = props.basePath) !== null && _c !== void 0 ? _c : (_d = parentRef === null || parentRef === void 0 ? void 0 : parentRef.value) === null || _d === void 0 ? void 0 : _d.address;
        };
        provide(SchemaSymbol, fieldSchemaRef);
        return function () {
            var basePath = getBasePath();
            var fieldProps = fieldPropsRef.value;
            var generateSlotsByProperties = function (scoped) {
                if (scoped === void 0) { scoped = false; }
                if (props.onlyRenderSelf)
                    return {};
                var properties = Schema.getOrderProperties(fieldSchemaRef.value);
                if (!properties.length)
                    return {};
                var renderMap = {};
                var setRender = function (key, value) {
                    if (!renderMap[key]) {
                        renderMap[key] = [];
                    }
                    renderMap[key].push(value);
                };
                properties.forEach(function (_a, index) {
                    var _b;
                    var item = _a.schema, name = _a.key;
                    var schema = item;
                    if (isFn(props.mapProperties)) {
                        var mapped = props.mapProperties(item, name);
                        if (mapped) {
                            schema = mapped;
                        }
                    }
                    if (isFn(props.filterProperties)) {
                        if (props.filterProperties(schema, name) === false) {
                            return null;
                        }
                    }
                    setRender((_b = schema['x-slot']) !== null && _b !== void 0 ? _b : 'default', function (field) {
                        var _a;
                        return h(RecursionField, {
                            key: "".concat(index, "-").concat(name),
                            attrs: {
                                schema: schema,
                                name: name,
                                basePath: (_a = field === null || field === void 0 ? void 0 : field.address) !== null && _a !== void 0 ? _a : basePath,
                            },
                            slot: schema['x-slot'],
                        }, {});
                    });
                });
                var slots = {};
                Object.keys(renderMap).forEach(function (key) {
                    var renderFns = renderMap[key];
                    slots[key] = scoped
                        ? function (_a) {
                            var field = _a.field;
                            return renderFns.map(function (fn) { return fn(field); });
                        }
                        : function () { return renderFns.map(function (fn) { return fn(); }); };
                });
                return slots;
            };
            var render = function () {
                if (!isValid(props.name))
                    return resolveEmptySlot(generateSlotsByProperties());
                if (fieldSchemaRef.value.type === 'object') {
                    if (props.onlyRenderProperties)
                        return resolveEmptySlot(generateSlotsByProperties());
                    return h(ObjectField, {
                        attrs: __assign(__assign({}, fieldProps), { name: props.name, basePath: basePath }),
                    }, generateSlotsByProperties(true));
                }
                else if (fieldSchemaRef.value.type === 'array') {
                    return h(ArrayField, {
                        attrs: __assign(__assign({}, fieldProps), { name: props.name, basePath: basePath }),
                    }, {});
                }
                else if (fieldSchemaRef.value.type === 'void') {
                    if (props.onlyRenderProperties)
                        return resolveEmptySlot(generateSlotsByProperties());
                    var slots = generateSlotsByProperties(true);
                    return h(VoidField, {
                        attrs: __assign(__assign({}, fieldProps), { name: props.name, basePath: basePath }),
                    }, slots);
                }
                return h(Field, {
                    attrs: __assign(__assign({}, fieldProps), { name: props.name, basePath: basePath }),
                }, {});
            };
            if (!fieldSchemaRef.value)
                return;
            return render();
        };
    },
};
export default RecursionField;
//# sourceMappingURL=RecursionField.js.map